import React, { useState } from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { DistributionType, FansType, HeatRecoveryPercentageTypes, VentilationSystemType, VentilationType } from 'constants/purchaseFlow'
import { GRAY_900 } from 'constants/styling/theme'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { Label } from 'components/common/Label'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIRadio } from 'components/common/MUIRadio'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import type { ToggleFilterValue } from 'components/common/ToggleFilter'

import type { EnergyVentilationView } from '../../energyCertificateOfNeed.codec'
import type { UseEnergyCertificateForm } from '../../useEnergyCertificateForm'
import { PRIMARY_FIELD_WIDTH } from './layout'

/**
 * The ventilation choices. Whether a system is available is not on the wire (it is inferred on decode),
 * so the set lives in local state, and deselecting the system clears its four fields in the same
 * update — otherwise a resumed draft would infer it back.
 */
export const VentilationSection: React.FC<{ initial: EnergyVentilationView; form: UseEnergyCertificateForm }> = ({ initial, form }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'energy_certificate_step.energy_heating_page.ventilation_system' })
  const [features, setFeatures] = useState<EnergyVentilationView>(initial)

  const commit = (next: EnergyVentilationView) => {
    setFeatures(next)
    form.patchVentilation(next)
  }

  const ventilationSystemValues: ToggleFilterValue<VentilationSystemType>[] = Object.values(VentilationSystemType).map((type) => ({
    value: type,
    displayName: t(`ventilation_system_type.${type}`),
  }))

  const selectSystem = (type: VentilationSystemType) => {
    const ventilationSystem = new Set(features.ventilationSystem)
    ventilationSystem.add(type)
    commit({ ...features, ventilationSystem })
  }

  const unselectSystem = (type: VentilationSystemType) => {
    const ventilationSystem = new Set(features.ventilationSystem)
    ventilationSystem.delete(type)
    // Its four fields go too, or decode would infer the system back on resume.
    if (type === VentilationSystemType.VENTILATION_SYSTEM_AVAILABLE) {
      commit({ ventilationSystem, distribution: null, ventilation: null, fans: null, heatRecovery: null })
      return
    }
    commit({ ...features, ventilationSystem })
  }

  const systemAvailable = features.ventilationSystem.has(VentilationSystemType.VENTILATION_SYSTEM_AVAILABLE)
  const showHeatRecovery = features.ventilation != null && features.ventilation !== VentilationType.EXHAUST_SYSTEM_NO_HEAT_RECOVERY

  return (
    <SectionedBorderBox title={<Typography variant="text-md" fontWeight={600} color={GRAY_900}>{t('ventilation_system_title')}</Typography>}>
      <Stack alignItems="flex-start" gap={1.6} width="100%">

        <Label text={t('ventilation_system_required')} />

        <SelectableButtonsGrid
          values={ventilationSystemValues}
          selectedValue={Array.from(features.ventilationSystem)}
          onSelect={selectSystem}
          onUnselect={unselectSystem}
          showCheckbox
          activeButtonClickable
        />

        {systemAvailable &&
          <BorderBoxWrapper padding={2} width="100%">

            <Stack gap=".8rem">
              <Label text={t('distribution')} />
              {Object.values(DistributionType).map((type) => (
                <Stack key={type} gap=".8rem" flexDirection="row">
                  <MUIRadio checked={features.distribution === type} onChange={() => commit({ ...features, distribution: type })} />
                  <Label color={GRAY_900} text={t(`distribution_type.${type}`)} />
                </Stack>
              ))}
            </Stack>

            <Stack gap=".8rem" paddingTop={1.6}>
              <Label text={t('ventilation')} />
              {Object.values(VentilationType).map((type) => (
                <Stack key={type} gap=".8rem" flexDirection="row">
                  <MUIRadio checked={features.ventilation === type} onChange={() => commit({ ...features, ventilation: type })} />
                  <Label color={GRAY_900} text={t(`ventilation_type.${type}`)} />
                </Stack>
              ))}
            </Stack>

            <Stack gap=".8rem" paddingTop={1.6}>
              <Label text={t('fans')} />
              {Object.values(FansType).map((type) => (
                <Stack key={type} gap=".8rem" flexDirection="row">
                  <MUIRadio checked={features.fans === type} onChange={() => commit({ ...features, fans: type })} />
                  <Label color={GRAY_900} text={t(`fans_type.${type}`)} />
                </Stack>
              ))}
            </Stack>

            {showHeatRecovery &&
              <Stack gap="1.8rem" paddingTop={1.6} paddingBottom={1.6} width={{ xs: '100%', lg: PRIMARY_FIELD_WIDTH }} position="relative">
                <MUIDropdown
                  sx={{ width: '100%' }}
                  button={(_isOpen, action) => (
                    <MUIDropdownInput readOnly label={t('heat_recovery')} type={DropdownInputTypes.SELECTOR} value={t(features.heatRecovery ? `heat_recovery_options.${features.heatRecovery}` : 'select_heat_recovery_option')} onClick={action} />
                  )}
                >
                  {Object.values(HeatRecoveryPercentageTypes).map((type) => (
                    <MUIDropdownItem key={type} onClick={() => commit({ ...features, heatRecovery: type })}>
                      <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`heat_recovery_options.${type}`)}</Typography>
                    </MUIDropdownItem>
                  ))}
                </MUIDropdown>
              </Stack>
            }

          </BorderBoxWrapper>
        }

      </Stack>
    </SectionedBorderBox>
  )
}
